import type { FC } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import {
  Add,
  ChangeHistory,
  CheckBoxOutlineBlank,
  Pause,
  PlayArrow,
  RadioButtonUnchecked,
} from '@mui/icons-material'

import { AgentMode } from '@/index'
import { AgentModeBadgeTransition } from '@/features/events/conversation/agent-mode-badges/AgentModeBadgeTransition'

import { HOME_CONTENT } from '../defaults'
import {
  cardSectionContainerSx,
  cardSectionTitleSx,
  cardSectionSubtitleSx,
  cardSectionCardsContainerSx,
  cardSx,
  cardTitleSx,
  cardDescriptionSx,
} from '../styles'

export const ConversationFeaturesSection: FC = () => (
  <Box
    component="section"
    role="region"
    aria-labelledby={SECTION_HEADING_ID}
    sx={cardSectionContainerSx}
  >
    <Typography id={SECTION_HEADING_ID} component="h2" sx={cardSectionTitleSx}>
      {HOME_CONTENT.conversationFeatures.title}
    </Typography>
    <Typography sx={cardSectionSubtitleSx}>
      {HOME_CONTENT.conversationFeatures.description}
    </Typography>

    <Box sx={cardSectionCardsContainerSx(1200)}>
      <StateOfPlayCard />
      <ModeAwareCard />
      <YourCallCard />
    </Box>
  </Box>
)

// ---------------------------------------------------------------------------
// Card 1 (Left): State of Play — Blocking Icons
// ---------------------------------------------------------------------------

const StateOfPlayCard: FC = () => {
  const { bash, question, plan } = useStateOfPlayAnimation()
  const theme = useTheme()

  return (
    <Box sx={cardSx}>
      <Typography sx={cardTitleSx}>{HOME_CONTENT.stateOfPlay.title}</Typography>
      <Typography sx={cardDescriptionSx}>{HOME_CONTENT.stateOfPlay.description}</Typography>
      <Box
        sx={demoAreaSx}
        role="img"
        aria-label="Demonstration of blocking event icons transitioning from awaiting action to acknowledged"
      >
        <BlockingIcon
          icon={ChangeHistory}
          phase={bash.phase}
          blockedColor={theme.palette.warning.main}
          acknowledgedColor={theme.palette.success.main}
        />
        <BlockingIcon
          icon={CheckBoxOutlineBlank}
          phase={plan.phase}
          blockedColor={PLAN_BLOCKED_COLOR}
          acknowledgedColor={theme.palette.success.main}
        />
        <BlockingIcon
          icon={RadioButtonUnchecked}
          phase={question.phase}
          blockedColor={theme.palette.warning.main}
          acknowledgedColor={theme.palette.success.main}
        />
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Card 2 (Center): Mode Control — Agent Mode Transition Demo
// ---------------------------------------------------------------------------

const MODE_DEMO_ARIA_LABEL =
  'Agent mode transitions cycling fluently through default, accept edits, plan, auto, and bypass permissions'
const MODE_DEMO_STATIC_ARIA_LABEL = 'Agent mode transition from default to accept edits'

const ModeAwareCard: FC = () => {
  const { slotA, slotB, activeSlot, isStatic } = useAgentModeAnimation()

  return (
    <Box sx={cardSx}>
      <Typography sx={cardTitleSx}>{HOME_CONTENT.modeControl.title}</Typography>
      <Typography sx={cardDescriptionSx}>{HOME_CONTENT.modeControl.description}</Typography>
      <Box
        sx={modeCrossfadeContainerSx}
        role="img"
        aria-label={isStatic ? MODE_DEMO_STATIC_ARIA_LABEL : MODE_DEMO_ARIA_LABEL}
      >
        <Box sx={crossfadeSlotSx(activeSlot === 0)}>
          <AgentModeBadgeTransition from={slotA.from} to={slotA.to} />
        </Box>
        <Box sx={crossfadeSlotSx(activeSlot === 1)}>
          <AgentModeBadgeTransition from={slotB.from} to={slotB.to} />
        </Box>
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Card 3 (Right): Your Call — Control Icons
// ---------------------------------------------------------------------------

const YourCallCard: FC = () => {
  const { pause, inject, resume, allGreen } = useYourCallAnimation()
  const theme = useTheme()

  return (
    <Box sx={cardSx}>
      <Typography sx={cardTitleSx}>{HOME_CONTENT.yourCall.title}</Typography>
      <Typography sx={cardDescriptionSx}>{HOME_CONTENT.yourCall.description}</Typography>
      <Box
        sx={demoAreaSx}
        role="img"
        aria-label="Demonstration of conversation control: pause agent, inject instructions, resume execution"
      >
        <ControlIcon
          icon={Pause}
          phase={pause.phase}
          activeColor={theme.palette.warning.main}
          allGreen={allGreen}
        />
        <ControlIcon
          icon={Add}
          phase={inject.phase}
          activeColor={theme.palette.info.main}
          allGreen={allGreen}
        />
        <ControlIcon
          icon={PlayArrow}
          phase={resume.phase}
          activeColor={theme.palette.success.main}
          allGreen={allGreen}
        />
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Blocking Icon (State of Play)
// ---------------------------------------------------------------------------

interface BlockingIconProps {
  readonly icon: typeof ChangeHistory
  readonly phase: 'blocked' | 'acknowledged'
  readonly blockedColor: string
  readonly acknowledgedColor: string
}

const BlockingIcon: FC<BlockingIconProps> = ({
  icon: Icon,
  phase,
  blockedColor,
  acknowledgedColor,
}) => {
  const color = phase === 'blocked' ? blockedColor : acknowledgedColor
  const iconSx = useMemo(
    () => (phase === 'blocked' ? blockingPulsingIconSx(color) : staticBlockingIconSx(color)),
    [phase, color],
  )

  return <Icon sx={iconSx} />
}

// ---------------------------------------------------------------------------
// Control Icon (Your Call)
// ---------------------------------------------------------------------------

type ControlPhase = 'idle' | 'active' | 'done'

interface ControlIconProps {
  readonly icon: typeof Pause
  readonly phase: ControlPhase
  readonly activeColor: string
  readonly allGreen: boolean
}

const ControlIcon: FC<ControlIconProps> = ({ icon: Icon, phase, activeColor, allGreen }) => {
  const theme = useTheme()

  const iconSx = useMemo(() => {
    if (phase === 'idle') return staticControlIconSx(theme.palette.text.disabled)
    if (phase === 'active') return controlPulsingIconSx(activeColor)
    return staticControlIconSx(allGreen ? theme.palette.success.main : activeColor)
  }, [phase, activeColor, allGreen, theme.palette.text.disabled, theme.palette.success.main])

  return <Icon sx={iconSx} />
}

// ---------------------------------------------------------------------------
// Hook: useStateOfPlayAnimation
// ---------------------------------------------------------------------------

interface BlockingIconState {
  readonly phase: 'blocked' | 'acknowledged'
}

interface StateOfPlayAnimationReturn {
  readonly bash: BlockingIconState
  readonly question: BlockingIconState
  readonly plan: BlockingIconState
}

interface BlockingAnimationPhase {
  readonly bash: 'blocked' | 'acknowledged'
  readonly question: 'blocked' | 'acknowledged'
  readonly plan: 'blocked' | 'acknowledged'
  readonly durationMs: number
}

type BlockingKey = 'bash' | 'question' | 'plan'
const BLOCKING_KEYS: readonly BlockingKey[] = ['bash', 'question', 'plan']

const buildBlockingPhases = (): BlockingAnimationPhase[] => {
  const order = shuffled(BLOCKING_KEYS)
  const base: Record<BlockingKey, 'blocked' | 'acknowledged'> = {
    bash: 'blocked',
    question: 'blocked',
    plan: 'blocked',
  }
  const phases: BlockingAnimationPhase[] = [{ ...base, durationMs: 3000 }]
  for (const key of order) {
    base[key] = 'acknowledged'
    phases.push({ ...base, durationMs: 2500 })
  }
  phases.push({
    bash: 'acknowledged',
    question: 'acknowledged',
    plan: 'acknowledged',
    durationMs: 1500,
  })
  return phases
}

const ALL_ACKNOWLEDGED: BlockingIconState = { phase: 'acknowledged' }

const useStateOfPlayAnimation = (): StateOfPlayAnimationReturn => {
  const [phaseIndex, setPhaseIndex] = useState(0)
  const phasesRef = useRef(buildBlockingPhases())

  const prefersReducedMotion = useRef(
    'matchMedia' in globalThis && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    if (prefersReducedMotion.current) return

    const currentPhase = phasesRef.current[phaseIndex]
    if (currentPhase === undefined) return

    const timerId = setTimeout(() => {
      const nextIndex = phaseIndex + 1
      if (nextIndex < phasesRef.current.length) {
        setPhaseIndex(nextIndex)
      } else {
        phasesRef.current = buildBlockingPhases()
        setPhaseIndex(0)
      }
    }, currentPhase.durationMs)

    return () => clearTimeout(timerId)
  }, [phaseIndex])

  if (prefersReducedMotion.current) {
    return { bash: ALL_ACKNOWLEDGED, question: ALL_ACKNOWLEDGED, plan: ALL_ACKNOWLEDGED }
  }

  const currentPhase = phasesRef.current[phaseIndex]
  if (currentPhase === undefined) {
    return { bash: ALL_ACKNOWLEDGED, question: ALL_ACKNOWLEDGED, plan: ALL_ACKNOWLEDGED }
  }

  return {
    bash: { phase: currentPhase.bash },
    question: { phase: currentPhase.question },
    plan: { phase: currentPhase.plan },
  }
}

// ---------------------------------------------------------------------------
// Hook: useAgentModeAnimation (two-slot crossfade)
// ---------------------------------------------------------------------------

interface ModeTransitionPhase {
  readonly from: AgentMode
  readonly to: AgentMode
}

interface AgentModeAnimationReturn {
  readonly slotA: ModeTransitionPhase
  readonly slotB: ModeTransitionPhase
  readonly activeSlot: 0 | 1
  readonly isStatic: boolean
}

const MODE_TRANSITION_PHASES: readonly ModeTransitionPhase[] = [
  { from: AgentMode.DEFAULT_MODE, to: AgentMode.AUTO_ACCEPT_EDITS_MODE },
  { from: AgentMode.AUTO_ACCEPT_EDITS_MODE, to: AgentMode.PLAN_MODE },
  { from: AgentMode.PLAN_MODE, to: AgentMode.AUTO_MODE },
  { from: AgentMode.AUTO_MODE, to: AgentMode.BYPASS_PERMISSIONS_MODE },
  { from: AgentMode.BYPASS_PERMISSIONS_MODE, to: AgentMode.DEFAULT_MODE },
]

const MODE_HOLD_MS = 3000
const MODE_CROSSFADE_MS = 500

const useAgentModeAnimation = (): AgentModeAnimationReturn => {
  const [activeSlot, setActiveSlot] = useState<0 | 1>(0)
  const [slotA, setSlotA] = useState(MODE_TRANSITION_PHASES[0]!)
  const [slotB, setSlotB] = useState(MODE_TRANSITION_PHASES[1]!)
  const nextIndexRef = useRef(2)
  const contentTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const prefersReducedMotion = useRef(
    'matchMedia' in globalThis && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    if (prefersReducedMotion.current) return

    const holdTimerId = setTimeout(() => {
      const newActive: 0 | 1 = activeSlot === 0 ? 1 : 0
      const oldActive = activeSlot
      setActiveSlot(newActive)

      contentTimerRef.current = setTimeout(() => {
        const nextPhase = MODE_TRANSITION_PHASES[nextIndexRef.current]!
        nextIndexRef.current = (nextIndexRef.current + 1) % MODE_TRANSITION_PHASES.length

        if (oldActive === 0) {
          setSlotA(nextPhase)
        } else {
          setSlotB(nextPhase)
        }
      }, MODE_CROSSFADE_MS + 50)
    }, MODE_HOLD_MS)

    return () => clearTimeout(holdTimerId)
  }, [activeSlot])

  useEffect(() => {
    return () => clearTimeout(contentTimerRef.current)
  }, [])

  if (prefersReducedMotion.current) {
    return {
      slotA: MODE_TRANSITION_PHASES[0]!,
      slotB: MODE_TRANSITION_PHASES[1]!,
      activeSlot: 0,
      isStatic: true,
    }
  }

  return { slotA, slotB, activeSlot, isStatic: false }
}

// ---------------------------------------------------------------------------
// Hook: useYourCallAnimation
// ---------------------------------------------------------------------------

interface ControlState {
  readonly phase: ControlPhase
}

interface YourCallAnimationReturn {
  readonly pause: ControlState
  readonly inject: ControlState
  readonly resume: ControlState
  readonly allGreen: boolean
}

interface ControlAnimationPhase {
  readonly pause: ControlPhase
  readonly inject: ControlPhase
  readonly resume: ControlPhase
  readonly durationMs: number
  readonly allGreen: boolean
}

type ControlKey = 'pause' | 'inject' | 'resume'
const CONTROL_KEYS: readonly ControlKey[] = ['pause', 'inject', 'resume']

const buildControlPhases = (): ControlAnimationPhase[] => {
  const order = shuffled(CONTROL_KEYS)
  const base: Record<ControlKey, ControlPhase> = { pause: 'idle', inject: 'idle', resume: 'idle' }
  const phases: ControlAnimationPhase[] = []
  for (const key of order) {
    base[key] = 'active'
    phases.push({ ...base, durationMs: 2500, allGreen: false })
    base[key] = 'done'
  }
  phases.push(
    { pause: 'done', inject: 'done', resume: 'done', durationMs: 2000, allGreen: true },
    { pause: 'done', inject: 'done', resume: 'done', durationMs: 1500, allGreen: true },
  )
  return phases
}

const ALL_DONE: ControlState = { phase: 'done' }

const useYourCallAnimation = (): YourCallAnimationReturn => {
  const [phaseIndex, setPhaseIndex] = useState(0)
  const phasesRef = useRef(buildControlPhases())

  const prefersReducedMotion = useRef(
    'matchMedia' in globalThis && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    if (prefersReducedMotion.current) return

    const currentPhase = phasesRef.current[phaseIndex]
    if (currentPhase === undefined) return

    const timerId = setTimeout(() => {
      const nextIndex = phaseIndex + 1
      if (nextIndex < phasesRef.current.length) {
        setPhaseIndex(nextIndex)
      } else {
        phasesRef.current = buildControlPhases()
        setPhaseIndex(0)
      }
    }, currentPhase.durationMs)

    return () => clearTimeout(timerId)
  }, [phaseIndex])

  if (prefersReducedMotion.current) {
    return { pause: ALL_DONE, inject: ALL_DONE, resume: ALL_DONE, allGreen: true }
  }

  const currentPhase = phasesRef.current[phaseIndex]
  if (currentPhase === undefined) {
    return { pause: ALL_DONE, inject: ALL_DONE, resume: ALL_DONE, allGreen: true }
  }

  return {
    pause: { phase: currentPhase.pause },
    inject: { phase: currentPhase.inject },
    resume: { phase: currentPhase.resume },
    allGreen: currentPhase.allGreen,
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SECTION_HEADING_ID = 'conversation-features-heading'
const PLAN_BLOCKED_COLOR = '#00acc1'

/** Fisher-Yates shuffle — returns a new array with randomized order */
const shuffled = <T,>(items: readonly T[]): T[] => {
  const result = [...items]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j]!, result[i]!]
  }
  return result
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const demoAreaSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 2.5,
  mt: 3,
  minHeight: 48,
}

const modeCrossfadeContainerSx: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  mt: 3,
  minHeight: 48,
  width: '100%',
}

const crossfadeSlotSx = (active: boolean): SxProps<Theme> => ({
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  opacity: active ? 1 : 0,
  '@media (prefers-reduced-motion: no-preference)': {
    transition: `opacity ${MODE_CROSSFADE_MS}ms ease-in-out`,
  },
})

const blockingPulsingIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '2rem',
  color,
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes stateOfPlayPulse': {
      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
      '50%': { opacity: 0.6, transform: 'scale(1.15)' },
    },
    animation: 'stateOfPlayPulse 2s ease-in-out infinite',
  },
})

const staticBlockingIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '2rem',
  color,
})

const controlPulsingIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '2rem',
  color,
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes yourCallPulse': {
      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
      '50%': { opacity: 0.6, transform: 'scale(1.15)' },
    },
    animation: 'yourCallPulse 2s ease-in-out infinite',
  },
})

const staticControlIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '2rem',
  color,
})
